import React, { useState, useEffect } from "react";
import {
  Layout,
  Table,
  Button,
  Tag,
  Space,
  Typography,
  Popconfirm,
  Tooltip,
  Select,
  Row,
  Col,
} from "antd";
import {
  PlusOutlined,
  FileTextOutlined,
  EditOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  SwapOutlined,
  ShoppingCartOutlined,
  EyeOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import {
  getInvoices,
  deleteInvoice,
  downloadInvoicePdf,
  sendEInvoice,
} from "../../../services/invoiceService";
import {
  getInvoiceStatusInfo,
  getEInvoiceStatusInfo,
} from "../../../constants/invoiceConstants";
import { formatPrice, formatDate } from "../../../utils/formatters";
import { useApiCall } from "../../../hooks/useApiCall";

import CreateInvoiceModal from "../../../components/modals/CreateInvoiceModal";
import CreateInvoiceFromOrderModal from "../../../components/modals/CreateInvoiceFromOrderModal";
import InvoiceStatusModal from "../../../components/modals/InvoiceStatusModal";
import UpdateInvoiceModal from "../../../components/modals/UpdateInvoiceModal";

const { Content } = Layout;
const { Title } = Typography;

const AdminInvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [statusFilter, setStatusFilter] = useState(null);
  const pageSize = 10;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFromOrderModal, setShowFromOrderModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const { loading, execute } = useApiCall();

  const fetchInvoices = async (page = currentPage) => {
    try {
      const data = await execute(() => getInvoices(page - 1, pageSize));
      setInvoices(data.content || []);
      setTotalElements(data.totalElements || 0);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [currentPage]);

  const handleDelete = async (id) => {
    try {
      await deleteInvoice(id);
      toast.success("Fatura silindi");
      fetchInvoices();
    } catch (error) {
      console.error("Error deleting invoice:", error);
    }
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      const response = await downloadInvoicePdf(invoice.id);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `fatura-${invoice.invoiceNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF indirildi");
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  const handleStatusChange = (invoice) => {
    setSelectedInvoice(invoice);
    setShowStatusModal(true);
  };

  const handleEdit = (invoice) => {
    setSelectedInvoice(invoice);
    setShowUpdateModal(true);
  };

  const handleSendEInvoice = async (invoice) => {
    try {
      await sendEInvoice(invoice.id);
      toast.success("E-fatura gönderildi");
      fetchInvoices();
    } catch (error) {
      console.error("Error sending e-invoice:", error);
    }
  };

  const handleModalSuccess = () => {
    setShowCreateModal(false);
    setShowFromOrderModal(false);
    setShowStatusModal(false);
    setShowUpdateModal(false);
    setSelectedInvoice(null);
    fetchInvoices();
  };

  const filteredInvoices = statusFilter
    ? invoices.filter((inv) => inv.status === statusFilter)
    : invoices;

  const columns = [
    {
      title: "Fatura No",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      render: (text, record) => (
        <Link to={`/admin/invoices/${record.id}`}>{text}</Link>
      ),
    },
    {
      title: "Müşteri",
      dataIndex: "customerName",
      key: "customerName",
      ellipsis: true,
    },
    {
      title: "Ara Toplam",
      dataIndex: "subtotal",
      key: "subtotal",
      render: (val) => formatPrice(val),
      align: "right",
    },
    {
      title: "KDV",
      dataIndex: "taxAmount",
      key: "taxAmount",
      render: (val) => formatPrice(val),
      align: "right",
    },
    {
      title: "Toplam",
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: (val) => (
        <Typography.Text strong>{formatPrice(val)}</Typography.Text>
      ),
      align: "right",
    },
    {
      title: "Durum",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const { color, text } = getInvoiceStatusInfo(status);
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "E-Fatura",
      dataIndex: "einvoiceStatus",
      key: "einvoiceStatus",
      render: (einvoiceStatus) => {
        if (!einvoiceStatus || einvoiceStatus === "NONE") return "-";
        const { color, text } = getEInvoiceStatusInfo(einvoiceStatus);
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "Tarih",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      render: (date) => formatDate(date),
    },
    {
      title: "Vade Tarihi",
      dataIndex: "dueDate",
      key: "dueDate",
      render: (date) => formatDate(date),
    },
    {
      title: "İşlemler",
      key: "actions",
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Görüntüle">
            <Link to={`/admin/invoices/${record.id}`}>
              <Button type="text" size="small" icon={<EyeOutlined />} />
            </Link>
          </Tooltip>
          {record.status === "DRAFT" && (
            <Tooltip title="Düzenle">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
          )}
          <Tooltip title="Durum Değiştir">
            <Button
              type="text"
              size="small"
              icon={<SwapOutlined />}
              onClick={() => handleStatusChange(record)}
            />
          </Tooltip>
          <Tooltip title="PDF İndir">
            <Button
              type="text"
              size="small"
              icon={<FilePdfOutlined />}
              onClick={() => handleDownloadPdf(record)}
            />
          </Tooltip>
          {record.status === "SENT" && (!record.einvoiceStatus || record.einvoiceStatus === "NONE") && (
            <Popconfirm
              title="Bu faturayı e-fatura olarak göndermek istiyor musunuz?"
              onConfirm={() => handleSendEInvoice(record)}
              okText="Evet"
              cancelText="Hayır"
            >
              <Tooltip title="E-Fatura Gönder">
                <Button
                  type="text"
                  size="small"
                  icon={<SendOutlined />}
                  style={{ color: "#1890ff" }}
                />
              </Tooltip>
            </Popconfirm>
          )}
          {record.status === "DRAFT" && (
            <Popconfirm
              title="Bu faturayı silmek istediğinize emin misiniz?"
              onConfirm={() => handleDelete(record.id)}
              okText="Evet"
              cancelText="Hayır"
            >
              <Tooltip title="Sil">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: "100vh", background: "#f0f2f5" }}>
      <Content style={{ padding: "24px" }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 24 }}
          >
            <Col>
              <Title level={2} style={{ margin: 0 }}>
                <FileTextOutlined style={{ color: "#1890ff", marginRight: 12 }} />
                Faturalar
              </Title>
            </Col>
            <Col>
              <Space>
                <Select
                  allowClear
                  placeholder="Durum filtrele"
                  style={{ width: 180 }}
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={[
                    { value: "DRAFT", label: "Taslak" },
                    { value: "SENT", label: "Gönderildi" },
                    { value: "PAID", label: "Ödendi" },
                    { value: "CANCELLED", label: "İptal" },
                    { value: "OVERDUE", label: "Vadesi Geçmiş" },
                  ]}
                />
                <Button
                  icon={<ShoppingCartOutlined />}
                  onClick={() => setShowFromOrderModal(true)}
                >
                  Siparişten Oluştur
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setShowCreateModal(true)}
                >
                  Yeni Fatura
                </Button>
              </Space>
            </Col>
          </Row>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
        >
          <Table
            columns={columns}
            dataSource={filteredInvoices}
            rowKey="id"
            loading={loading}
            bordered
            scroll={{ x: true }}
            pagination={
              statusFilter
                ? { pageSize: filteredInvoices.length || pageSize, showSizeChanger: false }
                : {
                    current: currentPage,
                    pageSize,
                    total: totalElements,
                    onChange: (page) => setCurrentPage(page),
                    showSizeChanger: false,
                    showTotal: (total) => `Toplam ${total} fatura`,
                  }
            }
          />
        </motion.div>

        <CreateInvoiceModal
          visible={showCreateModal}
          onCancel={() => setShowCreateModal(false)}
          onSuccess={handleModalSuccess}
        />
        <CreateInvoiceFromOrderModal
          visible={showFromOrderModal}
          onCancel={() => setShowFromOrderModal(false)}
          onSuccess={handleModalSuccess}
        />
        <InvoiceStatusModal
          visible={showStatusModal}
          invoice={selectedInvoice}
          onCancel={() => {
            setShowStatusModal(false);
            setSelectedInvoice(null);
          }}
          onSuccess={handleModalSuccess}
        />
        <UpdateInvoiceModal
          visible={showUpdateModal}
          invoice={selectedInvoice}
          onCancel={() => {
            setShowUpdateModal(false);
            setSelectedInvoice(null);
          }}
          onSuccess={handleModalSuccess}
        />
      </Content>
    </Layout>
  );
};

export default AdminInvoicesPage;
